import {
  CSSProperties,
  ReactNode,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { useInView } from 'react-intersection-observer';

export interface VideoProps {
  src: string;
  /** Ordered native sources; when provided, the video has no src attribute. */
  sources?: { src: string; type: string }[];
  className?: string;
  mode?: 'autoplay' | 'hover' | 'manual';
  contained?: boolean;
  loop?: boolean;
  inViewThreshold?: number | number[];
  /** Gate autoplay on intersection and document visibility, without an autoPlay attribute. */
  strictViewportPlayback?: boolean;
  /** Initial playback position; subsequent native loops play the entire video. */
  startTime?: number;
  poster?: string;
  preload?: 'none' | 'metadata' | 'auto';
  onFirstFrame?: () => void;
  onLoadedData?: () => void;
  onError?: (details?: { code?: number; currentSrc: string }) => void;
  onPlayRejected?: () => void;
  onStartStalled?: () => void;
  /** Allows a caller's button to play synchronously inside a user gesture. */
  manualPlayRef?: { current: (() => void) | null };
  onEnded?: () => void;
  children?: ReactNode;
  style?: CSSProperties;
  crossOrigin?: 'anonymous' | 'use-credentials';
}

export default function Video({
  src,
  sources,
  className,
  mode = 'autoplay',
  onLoadedData,
  onError,
  onPlayRejected,
  onStartStalled,
  manualPlayRef,
  onEnded,
  children,
  contained = false,
  loop = true,
  inViewThreshold = 0.5,
  strictViewportPlayback = false,
  startTime = 0,
  poster,
  preload,
  onFirstFrame,
  style,
  crossOrigin,
}: VideoProps) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const seekTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const seekTarget = useRef(startTime);
  const failedSources = useRef(new Set<string>());
  const errorReported = useRef(false);
  const fallbackRef = useRef<(video: HTMLVideoElement) => void>(() => {});
  const playRejectedRef = useRef(onPlayRejected);
  const startStalledRef = useRef(onStartStalled);
  playRejectedRef.current = onPlayRejected;
  startStalledRef.current = onStartStalled;
  const hovering = useRef(false);
  const seekStage = useRef<'metadata' | 'seeking' | 'decoding' | 'ready'>(
    startTime > 0 ? 'metadata' : 'ready',
  );
  const [playbackReady, setPlaybackReady] = useState(startTime <= 0);
  const sourceKey = sources?.map(({ src, type }) => `${src}:${type}`).join('|');
  const stallRecoveryEnabled = Boolean(onStartStalled);
  const [documentVisible, setDocumentVisible] = useState(
    () => typeof document === 'undefined' || !document.hidden,
  );
  const { ref: inViewRef, inView } = useInView({
    threshold:
      typeof inViewThreshold === 'number' ? [inViewThreshold] : inViewThreshold,
    skip: mode !== 'autoplay',
  });

  const clearSeekTimeout = () => {
    if (seekTimeout.current !== null) clearTimeout(seekTimeout.current);
    seekTimeout.current = null;
  };

  useLayoutEffect(() => {
    clearSeekTimeout();
    failedSources.current.clear();
    errorReported.current = false;
    seekTarget.current = startTime;
    if (startTime <= 0) {
      seekStage.current = 'ready';
      setPlaybackReady(true);
      return;
    }
    const video = videoRef.current;
    video?.pause();
    seekStage.current = 'metadata';
    setPlaybackReady(false);
    if (stallRecoveryEnabled) {
      seekTimeout.current = setTimeout(() => {
        if (seekStage.current === 'ready') return;
        startStalledRef.current?.();
        if (video && video.readyState >= HTMLMediaElement.HAVE_METADATA)
          fallbackRef.current(video);
      }, 4000);
    }
    return clearSeekTimeout;
  }, [src, sourceKey, startTime, stallRecoveryEnabled]);

  const frameReady = () => {
    if (seekStage.current !== 'decoding') return;
    seekStage.current = 'ready';
    clearSeekTimeout();
    setPlaybackReady(true);
    onLoadedData?.();
  };

  const fallBackToStart = (video: HTMLVideoElement) => {
    seekStage.current = 'decoding';
    seekTarget.current = 0;
    try {
      video.currentTime = 0;
    } catch {
      // Some streams do not support seeking; wait for their first frame.
    }
    if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) frameReady();
  };
  useLayoutEffect(() => {
    fallbackRef.current = fallBackToStart;
    if (!manualPlayRef) return;
    manualPlayRef.current = () => {
      const video = videoRef.current;
      if (!video) return;
      // Starting playback from a click can unlock metadata on Safari. Let the
      // metadata handler seek first; only abandon a seek already in progress.
      if (seekStage.current === 'seeking' || seekStage.current === 'decoding')
        fallBackToStart(video);
      void video.play()?.catch(() => playRejectedRef.current?.());
    };
    return () => {
      manualPlayRef.current = null;
    };
  });

  const reportError = (video: HTMLVideoElement) => {
    if (errorReported.current) return;
    errorReported.current = true;
    onError?.({ code: video.error?.code, currentSrc: video.currentSrc });
  };

  const playingFrameReady = (video: HTMLVideoElement) => {
    if (
      seekStage.current !== 'decoding' ||
      video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA ||
      Math.abs(video.currentTime - seekTarget.current) > 0.5
    )
      return;
    // A playing frame at the selected offset (or at zero after fallback) is
    // enough even if Safari omits another loadeddata/canplay event.
    frameReady();
  };

  useEffect(() => {
    if (!strictViewportPlayback) return;
    const updateVisibility = () => setDocumentVisible(!document.hidden);
    updateVisibility();
    document.addEventListener('visibilitychange', updateVisibility);
    return () =>
      document.removeEventListener('visibilitychange', updateVisibility);
  }, [strictViewportPlayback]);

  useEffect(() => {
    if (mode === 'hover') {
      if (startTime > 0 && playbackReady && hovering.current) {
        void videoRef.current?.play()?.catch(() => {
          // A hover-triggered play may be rejected by the browser.
          playRejectedRef.current?.();
        });
      }
      return;
    }
    if (mode !== 'autoplay') return;
    if (
      playbackReady &&
      inView &&
      (!strictViewportPlayback || documentVisible)
    ) {
      void videoRef.current?.play()?.catch(() => {
        playRejectedRef.current?.();
      });
    } else {
      videoRef.current?.pause();
    }
  }, [
    inView,
    mode,
    strictViewportPlayback,
    documentVisible,
    src,
    playbackReady,
    startTime,
  ]);

  return (
    <div ref={inViewRef} className={className} style={style}>
      <video
        ref={videoRef}
        onMouseEnter={() => {
          hovering.current = true;
          if (mode === 'hover') {
            if (startTime <= 0 || seekStage.current === 'ready')
              void videoRef.current?.play();
          }
        }}
        onMouseLeave={() => {
          hovering.current = false;
          if (mode === 'hover') {
            videoRef.current?.pause();
          }
        }}
        src={sources?.length ? undefined : src}
        poster={poster}
        preload={preload}
        crossOrigin={crossOrigin}
        className={`h-full w-full rounded-[inherit] object-${
          contained ? 'contain' : 'cover'
        }${startTime > 0 && !playbackReady ? ' opacity-0' : ''}`}
        muted
        playsInline
        loop={loop}
        disablePictureInPicture
        autoPlay={
          mode === 'autoplay' && !strictViewportPlayback && startTime <= 0
        }
        onContextMenu={(e) => e.preventDefault()}
        onLoadedMetadata={(event) => {
          if (seekStage.current !== 'metadata') return;
          const video = event.currentTarget;
          if (Number.isFinite(video.duration) && video.duration <= startTime) {
            fallBackToStart(video);
            return;
          }
          try {
            seekStage.current = 'seeking';
            video.currentTime = startTime;
          } catch {
            fallBackToStart(video);
          }
        }}
        onSeeked={(event) => {
          if (seekStage.current !== 'seeking') return;
          const video = event.currentTarget;
          if (
            !Number.isFinite(video.currentTime) ||
            Math.abs(video.currentTime - startTime) > 0.5
          ) {
            fallBackToStart(video);
            return;
          }
          seekStage.current = 'decoding';
          if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA)
            frameReady();
        }}
        onCanPlay={frameReady}
        onLoadedData={() => {
          if (startTime <= 0) onLoadedData?.();
          else frameReady();
        }}
        onTimeUpdate={(event) => playingFrameReady(event.currentTarget)}
        onPlaying={(event) => {
          playingFrameReady(event.currentTarget);
          if (seekStage.current !== 'ready') return;
          if (!onFirstFrame) return;
          const video = event.currentTarget;
          if (video.requestVideoFrameCallback) {
            video.requestVideoFrameCallback(() => {
              if (
                video.isConnected &&
                !video.paused &&
                seekStage.current === 'ready'
              )
                onFirstFrame();
            });
          } else {
            onFirstFrame();
          }
        }}
        onError={(event) => {
          // A rejected <source> can still be followed by a playable codec.
          if (event.target !== event.currentTarget) return;
          reportError(event.currentTarget);
        }}
        onEnded={() => onEnded?.()}
      >
        {sources?.map((source) => (
          <source
            key={source.src}
            src={source.src}
            type={source.type}
            onError={() => {
              failedSources.current.add(source.src);
              const video = videoRef.current;
              if (
                video &&
                failedSources.current.size === sources.length &&
                video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA
              )
                reportError(video);
            }}
          />
        ))}
        {children}
      </video>
    </div>
  );
}
